import { clearBrandingFonts, syncBrandingFonts } from '@/branding/fontManifest';
import { resolveBrandingPreset } from '@/branding/presets';
import { useConfigurationStore } from '@/store/configurationStore';
import type {
  Community,
  CommunityBranding,
  CommunityBrandingColorTokens,
  CommunityBrandingFontConfig,
  CommunityBrandingTypographyTokens,
} from '@/store/communityStore';

const colorVariableMap: Record<keyof Required<CommunityBrandingColorTokens>, string> = {
  bg: '--ck-bg',
  surface: '--ck-surface',
  surface_raised: '--ck-surface-raised',
  surface_hover: '--ck-surface-hover',
  surface_active: '--ck-surface-active',
  text: '--ck-text',
  muted: '--ck-muted',
  text_subtle: '--ck-text-subtle',
  disabled: '--ck-disabled',
  brand: '--ck-brand',
  brand_contrast: '--ck-brand-contrast',
  link: '--ck-link',
  link_hover: '--ck-link-hover',
  border: '--ck-border',
  border_strong: '--ck-border-strong',
  focus_ring: '--ck-focus-ring',
  success: '--ck-success',
  success_bg: '--ck-success-bg',
  warning: '--ck-warning',
  warning_bg: '--ck-warning-bg',
  danger: '--ck-danger',
  danger_bg: '--ck-danger-bg',
  info: '--ck-info',
  info_bg: '--ck-info-bg',
};

const typographyVariableMap: Record<keyof Required<CommunityBrandingTypographyTokens>, string> = {
  font_size_display: '--ck-font-size-display',
  font_size_headline: '--ck-font-size-headline',
  font_size_title: '--ck-font-size-title',
  font_size_body: '--ck-font-size-body',
  font_size_small: '--ck-font-size-small',
  leading_display: '--ck-leading-display',
  leading_headline: '--ck-leading-headline',
  leading_body: '--ck-leading-body',
  font_size_body_mobile: '--ck-font-size-body-mobile',
  leading_body_mobile: '--ck-leading-body-mobile',
  tracking_display: '--ck-tracking-display',
  tracking_headline: '--ck-tracking-headline',
};

function hexToRgbChannels(value: string) {
  const sanitized = value.trim().replace('#', '');
  const normalized =
    sanitized.length === 3
      ? sanitized
          .split('')
          .map((character) => character + character)
          .join('')
      : sanitized;

  if (!/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return null;
  }

  const r = parseInt(normalized.slice(0, 2), 16);
  const g = parseInt(normalized.slice(2, 4), 16);
  const b = parseInt(normalized.slice(4, 6), 16);
  return `${r} ${g} ${b}`;
}

function normalizeFontConfig(
  override: CommunityBrandingFontConfig | null | undefined,
  fallback: CommunityBrandingFontConfig,
): CommunityBrandingFontConfig {
  const candidate = {
    ...fallback,
    ...(override ?? {}),
  };

  if (candidate.source === 'builtin' && !candidate.builtin_key) {
    return fallback;
  }

  if (candidate.source === 'remote-css' && (!candidate.css_url || !candidate.family)) {
    return fallback;
  }

  return candidate;
}

function toFontStack(font: CommunityBrandingFontConfig) {
  const family = font.family?.trim();
  const fallback = font.fallback?.trim();

  if (!family) {
    return fallback ?? 'sans-serif';
  }

  return fallback ? `${family}, ${fallback}` : family;
}

function resolveBranding(branding: CommunityBranding | null | undefined, fallbackPresetKey: string | undefined) {
  const configurationStore = useConfigurationStore();
  const preset = resolveBrandingPreset(branding, configurationStore.brandingPresets, fallbackPresetKey);
  const isCustomBranding = branding?.mode === 'custom';
  const colors = {
    ...preset.tokens.colors,
    ...(isCustomBranding ? (branding?.tokens?.colors ?? {}) : {}),
  };
  const typography = {
    ...preset.tokens.typography,
    ...(isCustomBranding ? (branding?.tokens?.typography ?? {}) : {}),
  };
  const fonts = {
    heading: normalizeFontConfig(isCustomBranding ? branding?.fonts?.heading : null, preset.fonts.heading),
    body: normalizeFontConfig(isCustomBranding ? branding?.fonts?.body : null, preset.fonts.body),
  };

  return {
    presetKey: preset.key,
    colors,
    typography,
    fonts,
  };
}

export async function applyCommunityBranding(community: Community | null | undefined, fallbackPresetKey?: string) {
  if (!community) {
    return;
  }

  const resolved = resolveBranding(community.options?.branding, fallbackPresetKey);
  const root = document.documentElement;

  for (const [tokenKey, cssVariable] of Object.entries(colorVariableMap)) {
    const value = resolved.colors[tokenKey as keyof typeof resolved.colors];
    if (!value) {
      continue;
    }

    const rgbChannels = hexToRgbChannels(value);
    if (rgbChannels) {
      root.style.setProperty(cssVariable, rgbChannels);
    }
  }

  for (const [tokenKey, cssVariable] of Object.entries(typographyVariableMap)) {
    const value = resolved.typography[tokenKey as keyof typeof resolved.typography];
    if (value) {
      root.style.setProperty(cssVariable, value);
    }
  }

  await syncBrandingFonts([resolved.fonts.heading, resolved.fonts.body]);
  root.style.setProperty('--ck-font-family-heading', toFontStack(resolved.fonts.heading));
  root.style.setProperty('--ck-font-family-body', toFontStack(resolved.fonts.body));
  root.dataset.ckBrandingPreset = resolved.presetKey;
}

export function clearCommunityBranding() {
  clearBrandingFonts();
  const root = document.documentElement;

  for (const cssVariable of Object.values(colorVariableMap)) {
    root.style.removeProperty(cssVariable);
  }

  for (const cssVariable of Object.values(typographyVariableMap)) {
    root.style.removeProperty(cssVariable);
  }

  root.style.removeProperty('--ck-font-family-heading');
  root.style.removeProperty('--ck-font-family-body');
  delete root.dataset.ckBrandingPreset;
}
